/*
 * Copyright © HatioLab Inc. All rights reserved.
 */
import { ComponentNature, RectPath, Shape, sceneComponent, RealObject } from '@hatiolab/things-scene'
import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'

const NATURE: ComponentNature = {
  mutable: false,
  resizable: true,
  rotatable: true,
  properties: [
    {
      type: 'color',
      label: 'platform-color',
      name: 'platformColor',
      property: 'platformColor'
    }
  ],
  help: 'scene/component/vehicle'
}

const DEFAULT_PLATFORM_COLOR = '#333333'
const DEFAULT_BODY_COLOR = '#f0f0f0'
const BUMPER_COLOR = '#222222'

export class Vehicle3D extends RealObject {
  build() {
    super.build()

    var { width, height, depth, fillStyle, platformColor } = this.component.state

    var w = width
    var h = height
    var d = depth || 50

    // 비율: 하단 범퍼 10%, 본체 58%, 간격 4%, 플랫폼 28%
    var bumperH = d * 0.10
    var bodyH = d * 0.58
    var gapH = d * 0.04
    var platH = d * 0.28
    var totalH = bumperH + bodyH + gapH + platH

    var cornerR = Math.min(w, h, bodyH) * 0.12

    // ── 하단 범퍼 밴드 (본체보다 약간 넓게 돌출) ──
    var bumperPad = Math.min(w, h) * 0.03
    var bumperCornerR = Math.min(w + bumperPad * 2, h + bumperPad * 2, bumperH) * 0.2
    var bumperGeo = new RoundedBoxGeometry(w + bumperPad * 2, bumperH, h + bumperPad * 2, 4, bumperCornerR)
    var bumperMat = new THREE.MeshStandardMaterial({
      color: BUMPER_COLOR,
      roughness: 0.8,
      metalness: 0.1
    })
    var bumper = new THREE.Mesh(bumperGeo, bumperMat)
    bumper.position.y = bumperH / 2
    bumper.castShadow = true
    this.object3d.add(bumper)

    // ── 본체 (둥근 모서리, 밝은색) ──
    var bodyGeo = new RoundedBoxGeometry(w, bodyH, h, 4, cornerR)
    var bodyMat = new THREE.MeshStandardMaterial({
      color: fillStyle || DEFAULT_BODY_COLOR,
      roughness: 0.3,
      metalness: 0.05
    })
    var body = new THREE.Mesh(bodyGeo, bodyMat)
    body.position.y = bumperH + bodyH / 2
    body.castShadow = true
    this.object3d.add(body)

    // ── 상단 플랫폼 (본체보다 약간 안쪽, 어두운색) ──
    var platInset = Math.min(w, h) * 0.04
    var platCornerR = Math.min(w - platInset * 2, h - platInset * 2, platH) * 0.15
    var platGeo = new RoundedBoxGeometry(w - platInset * 2, platH, h - platInset * 2, 4, platCornerR)
    var platMat = new THREE.MeshStandardMaterial({
      color: platformColor || DEFAULT_PLATFORM_COLOR,
      roughness: 0.5,
      metalness: 0.15
    })
    var plat = new THREE.Mesh(platGeo, platMat)
    plat.position.y = bumperH + bodyH + gapH + platH / 2
    plat.castShadow = true
    this.object3d.add(plat)

    // 수직 중앙 정렬
    this.object3d.children.forEach(child => {
      child.position.y -= totalH / 2
    })
  }
}

/* ── 2D 렌더 ── */

function renderVehicle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  fillStyle: string,
  platformColor: string
) {
  var r = Math.min(w, h) * 0.1
  var bumperH = Math.max(3, h * 0.10)
  var platH = Math.max(4, h * 0.25)
  var platInset = Math.min(w, h) * 0.04

  // 하단 범퍼 밴드 (본체보다 약간 넓게)
  var bumperPad = Math.min(w, h) * 0.02
  ctx.fillStyle = BUMPER_COLOR
  ctx.beginPath()
  ctx.roundRect(x - bumperPad, y + h - bumperH, w + bumperPad * 2, bumperH, [0, 0, r, r])
  ctx.fill()

  // 본체 (둥근 사각형)
  ctx.fillStyle = fillStyle || DEFAULT_BODY_COLOR
  ctx.beginPath()
  ctx.roundRect(x, y + platH + 2, w, h - platH - bumperH - 2, r * 0.5)
  ctx.fill()

  ctx.strokeStyle = '#dddddd'
  ctx.lineWidth = 0.5
  ctx.beginPath()
  ctx.roundRect(x, y + platH + 2, w, h - platH - bumperH - 2, r * 0.5)
  ctx.stroke()

  // 상단 플랫폼 (약간 안쪽)
  ctx.fillStyle = platformColor || DEFAULT_PLATFORM_COLOR
  ctx.beginPath()
  ctx.roundRect(x + platInset, y, w - platInset * 2, platH, [r, r, 0, 0])
  ctx.fill()
}

@sceneComponent('vehicle')
export class Vehicle extends RectPath(Shape) {
  buildRealObject(): RealObject | undefined {
    return new Vehicle3D(this as any)
  }

  get nature() {
    return NATURE
  }

  render(ctx: CanvasRenderingContext2D) {
    var { left, top, width, height } = this.bounds
    var fillStyle = this.getState('fillStyle')
    var platformColor = this.getState('platformColor')

    renderVehicle(ctx, left, top, width, height, fillStyle, platformColor)

    ctx.beginPath()
    ctx.rect(left, top, width, height)
  }
}
